import { Link } from 'react-router-dom';
import Card from '@mui/material/Card';
import MuiLink from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { averageBreakdownRate, findReliabilityModel } from '../../domain/reliability';
import { useReliability } from '../../hooks/useReliability';
import { ScoreChip } from '../reliability/ReliabilityShared';

/** The model's ADAC/TÜV reliability score, from the same data as the reliability pages. */
export function CarReliabilityCard({ make, model }: { make: string; model: string }) {
  const { data, isLoading, isError } = useReliability();
  const entry = data ? findReliabilityModel(data.models, make, model) : null;
  const breakdowns = entry ? averageBreakdownRate(entry) : null;

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h6">Reliability</Typography>
        {entry && <ScoreChip score={entry.score} size="medium" />}
      </Stack>

      {isLoading && <Skeleton variant="text" />}

      {(isError || (data && !entry)) && (
        <Typography variant="body2" color="text.secondary">
          The ADAC and TÜV reports don&apos;t cover this model yet.
        </Typography>
      )}

      {entry && (
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            {entry.score !== null
              ? `Scores ${Math.round(entry.score)} out of 100, where 50 is as reliable as the average car of the same age.`
              : 'Not enough report data to score it.'}
          </Typography>
          {breakdowns !== null && (
            <Typography variant="body2" color="text.secondary">
              ADAC counts about {breakdowns.toFixed(1)} breakdowns per 1,000 cars.
            </Typography>
          )}
        </Stack>
      )}

      <MuiLink component={Link} to="/reliability" variant="body2" underline="hover" sx={{ display: 'inline-block', mt: 1.5 }}>
        How we score reliability
      </MuiLink>
    </Card>
  );
}
